import { HttpBackend, HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AdminUser } from '@reflact/ipkv-types/dist/Admin';
import { jwtDecode } from 'jwt-decode';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { TeilaufgabenCopyService } from '../routes/fallaufgaben-details/teilaufgaben-copy.service';

@Injectable({
  providedIn: 'root'
})
export class Auth {
  // Neccesary to work with interceptor
  private http = new HttpClient(inject(HttpBackend));
  private router = inject(Router);
  private teilaufgabenCopyService = inject(TeilaufgabenCopyService);

  public tokenLoaded$ = new BehaviorSubject(false);
  public initialized = false;

  constructor() {
    this.loadTokenFromLocalStorage();
  }

  public user = signal<AdminUser | null>(null);
  private readonly userTokenKey = "admintoken";
  private userToken: string | null = null;

  private async loadTokenFromLocalStorage() {
    let tokenFromLocalStorage: string | null = null;
    tokenFromLocalStorage = this.tryReadLocalStorageOrWarn();
    if (tokenFromLocalStorage) {
      if (this.isTokenValid(tokenFromLocalStorage)) {
        this.userToken = tokenFromLocalStorage;
        // Manually set headers, because http does not use interceptor
        try {
          const res = await firstValueFrom(this.http.get<{ adminUser: AdminUser, token: string }>("/api/admin/myuser", {
            headers: { "At": this.userToken }
          }))
          this.user.set(res.adminUser);
          this.userToken = res.token;
        } catch (e) {
          this.logoutUserByUnsetToken();
          this.router.navigate(["/login"])
        }
      } else {
        localStorage.removeItem(this.userTokenKey);
      }
    }
    this.initialized = true;
  }

  private isTokenValid(token: string | null) {
    if (token == null) {
      return false;
    }
    try {
      const decodedToken = jwtDecode(token);
      if (decodedToken.exp && decodedToken.exp * 1000 > new Date().getTime()) {
        return true;
      } else {
        return false;
      }
    } catch (e) {
      console.warn("found invalid token: ", token);
      return false;
    }
  }

  public logoutUserByUnsetToken() {
    this.userToken = null;
    this.user.set(null);
    this.teilaufgabenCopyService.clearClipboard();
    this.router.navigate(["/login"]);

    try {
      localStorage.removeItem(this.userTokenKey);
    } catch (e) {
      // that one does not matter;
    }
  }

  public setUserTokenAfterAuth(token: string, user: AdminUser) {
    if (!this.isTokenValid(token)) {
      throw new Error("Invalid usertoken provided: " + token);
    }
    this.userToken = token;
    this.user.set(user);
    this.tryWriteLocalStorageOrWarn(token);
  }

  public tryWriteLocalStorageOrWarn(token: string) {
    try {
      localStorage.setItem(this.userTokenKey, token);
      console.debug("LocalStorage updated with new token");
    } catch (e) {
      console.warn("Could not write Browsers Localstorage, session could expire", e);
    }
  }
  public tryReadLocalStorageOrWarn() {
    try {
      return localStorage.getItem(this.userTokenKey);
    } catch (e) {
      console.warn("Could not read Browsers Localstorage, session could expire", e);
    }
    return null;
  }


  public getUserToken() {
    return this.userToken;
  }
}
